import { useCallback, useEffect, useState } from 'react';
import { ArrowLeft } from 'shared/icons';
import api from '../../../services/api';
import Button from '../../ui/Button';
import { useActiveLocations } from '../lpnPickers';
import { ExtractListRows } from '../payload';
import AllocationBatchCard from './AllocationBatchCard';
import {
    AllocationItemsOf,
    CreateAllocationBatch,
    CreateAllocationRow,
    OrderAllocationItems,
    ParseAllocationPreview,
    SplitAllocationBatch,
    UpdateBatch,
} from './allocationModel';

/**
 * Tela de ALOCAÇÃO com prévia da JORNADA (§8.1.4 / M5 r1).
 *
 * O caminho é: escolher os LPNs e os destinos → ver a PRÉVIA da jornada de cada
 * pallet (o servidor monta as pernas e decide em quantas OSs isso cai) →
 * decidir se a jornada vai inteira para UM equipamento (`merge`) → fatiar se
 * quiser → comparar rotas sobre o mapa e escolher uma → gerar.
 *
 * ── Uma chamada de alocação por LEVA ────────────────────────────────────────
 * `POST /service-orders/allocation` recebe o MESMO corpo da prévia mais a rota
 * escolhida. Cada leva vira uma chamada própria: assim um erro na terceira
 * leva não desfaz nem duvida das duas que já entraram — as OSs de cada jornada
 * nascem atômicas no servidor, e a tela vira recibo do que passou.
 *
 * ⚠️ `merge_into_single_order` viaja nas DUAS chamadas (prévia e confirmação).
 * Se ele fosse só da confirmação, a tela mostraria uma divisão que o `POST`
 * não faria — e prévia que não descreve o que vai acontecer é pior que prévia
 * nenhuma.
 */
const AllocationWorkspace = ({ label, canReadLpns = false, canReadLocations = false, onBack, onGenerated }) => {
    const [batches, setBatches] = useState(() => [CreateAllocationBatch()]);
    const [confirmingKey, setConfirmingKey] = useState(null);
    const [lpnSuggestions, setLpnSuggestions] = useState([]);

    const locations = useActiveLocations(canReadLocations);

    // Sugestões de LPN são AJUDA, não requisito: sem `wms:manage:lpns` a lista
    // simplesmente não aparece e o Supervisor digita o código.
    useEffect(() => {
        if (!canReadLpns) {
            setLpnSuggestions([]);
            return undefined;
        }
        let alive = true;
        api.getWarehouseLpns({ status: 'ACTIVE', limit: 200 })
            .then((data) => {
                if (alive) {
                    setLpnSuggestions(ExtractListRows(data, ['lpns']));
                }
            })
            .catch(() => {
                if (alive) {
                    setLpnSuggestions([]);
                }
            });
        return () => {
            alive = false;
        };
    }, [canReadLpns]);

    const mutate = useCallback((batchKey, mutation) => {
        setBatches((previous) => UpdateBatch(previous, batchKey, mutation));
    }, []);

    const changeRow = (batchKey, rowKey, patch) => {
        mutate(batchKey, (batch) => ({
            ...batch,
            rows: batch.rows.map((row) => (row.key === rowKey ? { ...row, ...patch } : row)),
            // Mexeu nas linhas: a prévia e a rota escolhida deixaram de valer.
            preview: null,
            previewError: null,
            routeAlgorithm: null,
            manualSequence: null,
        }));
    };

    const addRow = (batchKey) => {
        mutate(batchKey, (batch) => ({ ...batch, rows: [...batch.rows, CreateAllocationRow()], preview: null }));
    };

    const removeRow = (batchKey, rowKey) => {
        mutate(batchKey, (batch) => ({
            ...batch,
            rows: batch.rows.filter((row) => row.key !== rowKey),
            preview: null,
            routeAlgorithm: null,
        }));
    };

    const split = (batchKey, selectedRowKeys) => {
        setBatches((previous) => SplitAllocationBatch(previous, batchKey, selectedRowKeys));
    };

    const preview = async (batchKey, { merge = null } = {}) => {
        const batch = batches.find((entry) => entry.key === batchKey);
        const items = AllocationItemsOf(batch);
        if (items.length === 0) {
            return;
        }
        const mergeIntoSingleOrder = merge === null ? batch.mergeIntoSingleOrder === true : merge === true;
        mutate(batchKey, (entry) => ({
            ...entry,
            mergeIntoSingleOrder,
            previewing: true,
            previewError: null,
        }));
        try {
            // `allocations` é o nome do contrato (não `items`): o backend
            // recusa qualquer outra chave. `merge_into_single_order` vai JUNTO
            // na prévia — ela tem de descrever exatamente as OSs que a
            // confirmação vai gerar.
            const data = await api.previewWarehouseAllocation({
                allocations: items,
                merge_into_single_order: mergeIntoSingleOrder,
            });
            mutate(batchKey, (entry) => ({
                ...entry,
                previewing: false,
                preview: ParseAllocationPreview(data),
            }));
        } catch (error) {
            mutate(batchKey, (entry) => ({
                ...entry,
                previewing: false,
                preview: null,
                previewError: error?.message || label('wms.common.load_error'),
            }));
        }
    };

    /**
     * "Juntar numa OS só" (M5-1) — e a prévia é REFEITA na hora.
     *
     * Sem refazer, a tela mostraria a jornada dividida enquanto o `POST`
     * geraria uma OS única: a prévia deixaria de ser prévia. Como o split é
     * decisão do BACKEND, quem responde o que muda é ele.
     */
    const toggleMerge = (batchKey) => {
        const batch = batches.find((entry) => entry.key === batchKey);
        preview(batchKey, { merge: !(batch?.mergeIntoSingleOrder === true) });
    };

    const chooseRoute = (batchKey, algorithm, manualSequence, routeSequence) => {
        mutate(batchKey, (entry) => ({
            ...entry,
            routeAlgorithm: algorithm,
            manualSequence: manualSequence ?? null,
            // A ordem que o método escolhido produziu. É ELA que vai para o
            // backend (ver `confirm`) — a confirmação não tem campo de
            // sequência, e a ordem de envio é o que grava a `seq` dos itens.
            routeSequence: Array.isArray(routeSequence) ? routeSequence : null,
        }));
    };

    const confirm = async (batchKey) => {
        const batch = batches.find((entry) => entry.key === batchKey);
        const items = AllocationItemsOf(batch);
        if (items.length === 0) {
            return;
        }
        setConfirmingKey(batchKey);
        mutate(batchKey, (entry) => ({ ...entry, error: null }));
        try {
            // ⚠️ A ORDEM DE ENVIO É A ROTA. A confirmação não tem campo de
            // sequência de propósito: o backend grava a `seq` de cada item na
            // ordem em que as linhas chegam, e essa `seq` é a amarração do par
            // LIFT+TRANSPORT (reordenar lá dentro emparelharia itens errados).
            // Então quem aplica a escolha do comparador é esta linha — sem ela,
            // escolher rota não mudaria absolutamente nada no que é gravado.
            const payload = {
                allocations: OrderAllocationItems(items, batch.routeSequence),
                ...(batch.routeAlgorithm ? { route_algorithm: batch.routeAlgorithm } : {}),
                // A MESMA decisão que a prévia mostrou. Mandar diferente aqui
                // faria a tela ter prometido OSs que o banco não vai gerar.
                merge_into_single_order: batch.mergeIntoSingleOrder === true,
            };
            const result = await api.createWarehouseAllocation(payload);
            // A leva vira RECIBO (códigos das OSs geradas, ali mesmo). É melhor
            // confirmação que um aviso passageiro: fica na tela, nomeia o que
            // nasceu e trava a edição do que já foi gravado.
            mutate(batchKey, (entry) => ({ ...entry, result }));
            onGenerated?.();
        } catch (error) {
            mutate(batchKey, (entry) => ({ ...entry, error: error?.message || label('wms.common.load_error') }));
        } finally {
            setConfirmingKey(null);
        }
    };

    return (
        <div className="space-y-4">
            <div className="flex flex-wrap items-start justify-between gap-3">
                <div className="max-w-3xl space-y-1">
                    <h2 className="text-xl font-bold text-black dark:text-white">{label('wms.allocation.title')}</h2>
                    <p className="text-sm text-mono-600 dark:text-mono-400">{label('wms.allocation.description')}</p>
                </div>
                <Button variant="secondary" onClick={onBack}>
                    <ArrowLeft size={16} className="mr-1.5" />
                    {label('wms.location_create.back')}
                </Button>
            </div>

            {batches.map((batch, index) => (
                <AllocationBatchCard
                    key={batch.key}
                    batch={batch}
                    index={index}
                    total={batches.length}
                    label={label}
                    lpnSuggestions={lpnSuggestions}
                    locationSuggestions={locations.rows}
                    confirming={confirmingKey === batch.key}
                    onChangeRow={(rowKey, patch) => changeRow(batch.key, rowKey, patch)}
                    onAddRow={() => addRow(batch.key)}
                    onRemoveRow={(rowKey) => removeRow(batch.key, rowKey)}
                    onSplit={(selectedRowKeys) => split(batch.key, selectedRowKeys)}
                    onPreview={() => preview(batch.key)}
                    onToggleMerge={() => toggleMerge(batch.key)}
                    onChooseRoute={(algorithm, manualSequence, routeSequence) =>
                        chooseRoute(batch.key, algorithm, manualSequence, routeSequence)
                    }
                    onConfirm={() => confirm(batch.key)}
                />
            ))}
        </div>
    );
};

export default AllocationWorkspace;
